<template>
    <div :class="['microlearning-toc-item', {'not-logged': disabled}, {'is-selected': isItemSelected}]">
        <div class="microlearning-toc-item__section" v-if="isLabel"> {{ title }}</div>
        <div
            v-else role="button"
            :class="['microlearning-toc-item__content', {'not-logged': disabled}, {'is-selected': isItemSelected}]"
            @click="assignSectionInfo"
        >
            <span class="microlearning-toc-item__icon" :class="[ {'is-selected': isItemSelected}, getSectionIcon]"></span>
            <div class="microlearning-toc-item__timecode-container">
                <span class="microlearning-toc-item__title"> {{ title }} </span>
                <span v-if="this.sectionType === 'video'" class="microlearning-toc-item__time"> {{ convertTimeToMinutesAndHours(time) }} </span>
            </div>
            <span class="icon-checked" v-if="isItemProgressEnough && !disabled || isSectionWatched || isQuizzPassed"></span>
            <span v-if="isItemInProgress && !isItemProgressEnough">
                <Progress
                    class="progress-circle"
                    :radius="8"
                    strokeColor="#3C6E71"
                    v-bind:value="progressPercent"
                    :transitionDuration="4000">
                    <div class="content"></div>
                </Progress>
            </span>
            <span class="icon-circle" v-if="(!isItemInProgress && !isSectionWatched && !isItemProgressEnough && this.sectionType === 'video') || this.section.type === 'microtest'"></span>
        </div>
        </div>
</template>

<script>
    import Progress from 'easy-circular-progress'
    import rangeHandler from 'helpers/rangeHandler'
    import { mapGetters } from 'vuex'
    import microLearningHelper from 'components/shared/mixins/microLearningHelper'

    export default {
        name: 'MicroLearningItem',
        mixins: [ microLearningHelper ],
        props: {
            time: Number,
            title: String,
            videoTimeCompleted: {
                type: Boolean,
                default: false
            },
            disabled: {
                type: Boolean,
                default: false
            },
            allCurrentlyWatchedSequences: {
                type: Array,
                default: () => []
            },
            sectionType: String,
            section: Object,
            quizzesResults: Array,
            disabledByMicrotest: {
                type: Boolean,
                default: false
            }
        },
        components: {
            Progress,
        },
        data () {
            return {
                progressPercent: 0,
                videoTimeRange: [
                    0,
                    this.time || 99999999999999
                ],
                isCompleted: false
            }
        },
        computed: {
            ...mapGetters({
                microLearningSection: 'MicroLearning/getMicroLearningSection',
                currentSessionSequences: 'MicroLearning/getCurrentSessionSequences',
                isLoggedIn: 'isLoggedIn'
            }),
            isLabel () {
                return this.time === ''
            },
            isSectionWatched () {
                if (this.sectionType === 'microtest') {
                    return this.isQuizPassed
                }
                if (!this.currentSessionSequences[this.section.elementId] || this.disabled) {
                    return false
                }
                const currentWatched = rangeHandler.getOverlappingLength(this.currentSessionSequences[this.section.elementId], this.videoTimeRange)
                return Math.max(Math.round(currentWatched / this.time * 100)) > 85
            },
            isItemSelected () {
                if (this.microLearningSection === null || this.disabled) {
                    return false
                }
                return this.microLearningSection.elementId === this.section.elementId
            },
            isItemInProgress () {
                return !this.isLabel && this.progressPercent > 0 && !this.disabled && this.progressPercent !== 100 && this.isItemSelected
            },
            // eslint-disable-next-line vue/return-in-computed-property
            getSectionIcon () {
                switch (this.sectionType) {
                case 'video':
                    return 'icon-video'
                case 'assignment':
                    return 'icon-bulb'
                case 'file':
                    return 'icon-file-text'
                case 'microtest':
                    return 'icon-memo-circle-check'
                }
            },
            isItemProgressEnough () {
                return this.progressPercent > 85
            },
            isQuizzPassed () {
                const quizz = this.quizzesResults.find((quizz) => {
                    return quizz.elementId === this.section.elementId
                })
                return quizz && quizz.passed
            }
        },
        watch: {
            allCurrentlyWatchedSequences: {
                handler () {
                    if (!this.isItemSelected || !this.currentSessionSequences[this.microLearningSection.elementId]) {
                        return false
                    }
                    const currentWatched = rangeHandler.getOverlappingLength(this.currentSessionSequences[this.section.elementId], this.videoTimeRange)
                    this.progressPercent = Math.max(Math.round(currentWatched / this.time * 100))
                },
                immediate: true
            },
            isItemProgressEnough: {
                handler () {
                    if (this.isItemSelected && this.isItemProgressEnough === true && !this.isCompleted) {
                        this.$store.dispatch('VideoPlayer/persistPlays')
                        this.isCompleted = true
                    }
                },
                immediate: true
            }
        },
        methods: {
            convertTimeToSeconds (time) {
                if (this.sectionType !== 'video') {
                    return false
                }
                if (time.toString().indexOf(':')) {
                    return time
                }
                let splittedTime = time.split(':')
                let hoursInseconds = (parseInt(splittedTime[0]) * 60 * 60)
                let minutesInseconds = (parseInt(splittedTime[1]) * 60)
                let seconds = parseInt(splittedTime[2])
                return hoursInseconds + minutesInseconds + seconds
            },
            async assignSectionInfo () {
                // Return this if we want to have the disabled for not logged in users
                // if (!this.isLoggedIn) {
                //     return
                // }
                this.$router.replace({ name: this.getCorrectRouteForMicroLearning, query: { element: this.section.elementId } })
                if (!this.disabled) {
                    await this.$store.dispatch('MicroLearning/assignMicroLearningSection', this.section)
                    if (this.sectionType === 'video') {
                        await this.$store.dispatch('Courses/changeCourseVideo', this.section)
                        this.$emit('reInitializeVideo')
                    }
                    this.$emit('closeOtherAccordionsThanCurrent')
                }
                if (this.disabledByMicrotest && this.isLoggedIn) {
                    this.$modal.show('quiz-notification-modal')
                    this.$store.dispatch('Quiz/setLastClickedMicroLearningSection', this.section)
                } else if (!this.isLoggedIn && (this.sectionType === 'microtest' || this.disabledByMicrotest)) {
                    this.$modal.show('shown-after-demo-watched')
                }
                document.documentElement.scrollTop = 0
            }
        }
    }
</script>

<style lang="scss" scoped>
    .microlearning-toc-item {
        display: flex;
        flex-direction: column;
        font-size: 1.3rem;
        position: relative;
        color: $white;
        padding: 0 10px;

        &__section {
            color: $white;
            padding: 15px 15px;
        }

        &__content {
            display: flex;
            padding: 14px 10px;
            background: $white;
            color: $black;
            position: relative;
            flex-direction: row;
            align-items: center;
            border-radius: 4px;

            &.not-logged {
                opacity: 0.3;
            }

            &:hover {
                cursor: pointer;
                // @include white(background, 0.1);
            }
            &.is-selected {
                background: $light_blue;
            }
        }

        &__timecode-container {
            flex-direction: column;
            display: flex;
        }

        &__time {
            margin: 0 10px 0 0;
            width: 100%;
            font-size: 1.4rem;
        }

        &__title {
            [class*="desktop_xxxl"] & {
                max-width: 270px;
            }
            font-size: 1.6rem;
            max-width: 190px;
        }
        &__icon {
            margin: 0 16px 0 0;
            font-size: 1.8rem;
            color: $lavender;
        }

        .icon-checked, .icon-circle {
            color: $william;
            position: absolute;
            right: 7px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 1.8rem;
        }

        .icon-circle {
            color: $emperor;
        }

        .progress-circle {
            position: absolute;
            right: 5px;
            top: 13px;
        }
    }
</style>
